import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Send, Paperclip, Search, X, Reply, File, Mic, Trash2, StopCircle, ChevronUp, ChevronDown } from 'lucide-react';

interface ChatWindowProps {
    conversationId: string | null;
}

interface Attachment {
    type: 'image' | 'video' | 'file' | 'audio';
    url: string;
    name: string;
    size: number;
}

interface Message {
    id: string;
    content: string;
    sender_id: string;
    created_at: string;
    reply_to_id?: string | null;
    attachments?: Attachment[] | null;
    sender: {
        name: string;
        avatar?: string;
    };
    reply_to?: {
        id: string;
        content: string;
        sender: {
            name: string;
        }
    } | null;
    is_read: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ conversationId }) => {
    const { user } = useAuth();
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [otherParticipantLastRead, setOtherParticipantLastRead] = useState<string | null>(null);
    const [replyTo, setReplyTo] = useState<Message | null>(null);
    const [uploading, setUploading] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);

    // Search State
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchMatches, setSearchMatches] = useState<string[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);

    // Voice Recording State
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const mimeTypeRef = useRef<string>('audio/webm'); // Default fallback

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (conversationId) {
            fetchMessages(conversationId);
            fetchParticipants(conversationId);
            subscribeToMessages(conversationId);
            subscribeToParticipants(conversationId);
            markAsRead(conversationId);
        } else {
            setMessages([]);
            setOtherParticipantLastRead(null);
        }
    }, [conversationId]);

    // Mark as read when new messages arrive and we are viewing
    useEffect(() => {
        if (conversationId && messages.length > 0) {
            markAsRead(conversationId);
        }
        if (!isSearchOpen && !searchQuery) {
            scrollToBottom();
        }
    }, [messages, conversationId, isSearchOpen, searchQuery]);

    // Paste event listener
    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            if (!conversationId) return;
            const items = e.clipboardData?.items;
            if (!items) return;

            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const blob = items[i].getAsFile();
                    if (blob) {
                        setPendingAttachments(prev => [...prev, blob]);
                    }
                }
            }
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [conversationId]);

    // Recording Timer Effect
    useEffect(() => {
        if (isRecording) {
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
            setRecordingTime(0);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isRecording]);

    // Search Logic
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchMatches([]);
            setCurrentMatchIndex(-1);
            return;
        }

        // Find matches (reverse to match WhatsApp style "up" = back in time)
        // We want matches ordered by occurrence in the message list (oldest -> newest) normally,
        // but for "Next/Prev" navigation, usually we jump between them.
        // Let's store them ordered by message date (default list order).
        // UP arrow (Previous) -> Go to index - 1.
        // DOWN arrow (Next) -> Go to index + 1.
        // Wait, "Up" physically means "older" in a chat list (usually).
        // If matches = [Old1, Old2, New1, New2]
        // If I am at New2 (Index 3).
        // UP checks Index 2.

        // Actually, let's reverse the matches array so Index 0 is the NEWEST match.
        // Match[0] = Newest. Match[1] = Older.
        // Start at Index 0 (Newest match).
        // "Up" arrow (Previous match in time) -> Index + 1.
        // "Down" arrow (Next match in time) -> Index - 1.

        const matches = messages
            .filter(msg => {
                const content = msg.content?.toLowerCase() || '';
                const sender = msg.sender?.name?.toLowerCase() || '';
                const query = searchQuery.toLowerCase();
                return content.includes(query) || sender.includes(query);
            })
            .map(msg => msg.id)
            .reverse(); // Newest first

        setSearchMatches(matches);
        setCurrentMatchIndex(matches.length > 0 ? 0 : -1);
    }, [searchQuery, messages]);

    // Scroll to active match
    useEffect(() => {
        if (currentMatchIndex >= 0 && searchMatches[currentMatchIndex]) {
            scrollToMessage(searchMatches[currentMatchIndex]);
        }
    }, [currentMatchIndex, searchMatches]);

    const handleNextMatch = () => { // Go "Up" (Older)
        if (searchMatches.length === 0) return;
        setCurrentMatchIndex(prev => Math.min(prev + 1, searchMatches.length - 1));
    };

    const handlePrevMatch = () => { // Go "Down" (Newer)
        if (searchMatches.length === 0) return;
        setCurrentMatchIndex(prev => Math.max(prev - 1, 0));
    };

    const highlightText = (text: string, highlight: string) => {
        if (!highlight.trim()) return text;
        const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === highlight.toLowerCase()
                ? <span key={i} className="bg-yellow-200 dark:bg-yellow-800 text-gray-900 dark:text-gray-100 font-bold px-0.5 rounded">{part}</span>
                : part
        );
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Determine supported MIME type
            const mimeTypes = [
                'audio/webm;codecs=opus',
                'audio/webm',
                'audio/mp4',
                'audio/ogg',
                'audio/wav'
            ];

            const supportedType = mimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || '';
            if (supportedType) {
                mimeTypeRef.current = supportedType;
            }

            const options = supportedType ? { mimeType: supportedType } : undefined;
            const recorder = new MediaRecorder(stream, options);
            const chunks: BlobPart[] = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.push(e.data);
            };

            recorder.onstop = async () => {
                const type = mimeTypeRef.current || 'audio/webm';
                const blob = new Blob(chunks, { type });
                // Extension based on type
                const ext = type.includes('mp4') ? 'mp4' : type.includes('wav') ? 'wav' : type.includes('ogg') ? 'ogg' : 'webm';
                const file = new File([blob], `voice-message-${Date.now()}.${ext}`, { type } as any);
                setPendingAttachments([file]);
            };

            recorder.start();
            setMediaRecorder(recorder);
            setIsRecording(true);
        } catch (err) {
            console.error('Microphone error:', err);
            alert('Mikrofon erişimi sağlanamadı.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
            mediaRecorder.stream.getTracks().forEach(track => track.stop());
            setIsRecording(false);
        }
    };

    const cancelRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
            mediaRecorder.stream.getTracks().forEach(track => track.stop());
            setIsRecording(false);
            setRecordingTime(0);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const scrollToMessage = (messageId: string) => {
        const element = document.getElementById(`message-${messageId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add highlight effect
            element.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
            setTimeout(() => {
                element.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
            }, 2000);
        }
    };

    const fetchParticipants = async (id: string) => {
        try {
            const { data, error } = await supabase
                .from('chat_participants')
                .select('user_id, last_read_at')
                .eq('conversation_id', id);

            if (error) throw error;

            const other = data.find(p => p.user_id !== user?.id);
            if (other) {
                setOtherParticipantLastRead(other.last_read_at);
            }
        } catch (error) {
            console.error('Error fetching participants:', error);
        }
    };

    const subscribeToParticipants = (id: string) => {
        const subscription = supabase
            .channel(`public:chat_participants:conversation_id=eq.${id}`)
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'chat_participants',
                filter: `conversation_id=eq.${id}`
            }, (payload) => {
                const updated = payload.new as any;
                if (updated.user_id !== user?.id) {
                    setOtherParticipantLastRead(updated.last_read_at);
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
        };
    };

    const markAsRead = async (id: string) => {
        if (!user) return;
        try {
            await supabase
                .from('chat_participants')
                .update({ last_read_at: new Date().toISOString() })
                .eq('conversation_id', id)
                .eq('user_id', user.id);
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const fetchMessages = async (id: string) => {
        setLoading(true);
        try {
            // 1. Fetch main messages
            const { data, error } = await supabase
                .from('chat_messages')
                .select(`
                    id,
                    content,
                    sender_id,
                    created_at,
                    is_read,
                    reply_to_id,
                    attachments,
                    sender:profiles(name, avatar)
                `)
                .eq('conversation_id', id)
                .order('created_at', { ascending: true });

            if (error) throw error;

            // 2. Fetch reply details manually (to avoid self-referencing FK ambiguity)
            const replyIds = [...new Set(data.map(m => m.reply_to_id).filter(Boolean))];
            let replyMap: Record<string, any> = {};

            if (replyIds.length > 0) {
                const { data: replies, error: replyError } = await supabase
                    .from('chat_messages')
                    .select('id, content, sender:profiles(name)')
                    .in('id', replyIds);

                if (!replyError && replies) {
                    replyMap = replies.reduce((acc: any, r: any) => ({ ...acc, [r.id]: r }), {});
                }
            }

            // 3. Merge reply details
            const messagesWithReplies = data.map(m => ({
                ...m,
                reply_to: m.reply_to_id ? replyMap[m.reply_to_id] : null
            }));

            setMessages(messagesWithReplies as any);
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const subscribeToMessages = (id: string) => {
        const subscription = supabase
            .channel(`public:chat_messages:conversation_id=eq.${id}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'chat_messages',
                filter: `conversation_id=eq.${id}`
            }, async (payload) => {
                const newMsg = payload.new as any;

                // 1. Fetch the new message data
                const { data, error } = await supabase
                    .from('chat_messages')
                    .select(`
                        id,
                        content,
                        sender_id,
                        created_at,
                        is_read,
                        reply_to_id,
                        attachments,
                        sender:profiles(name, avatar)
                    `)
                    .eq('id', newMsg.id)
                    .single();

                if (data) {
                    // 2. Fetch reply info if exists
                    let replyInfo = null;
                    if (data.reply_to_id) {
                        const { data: rData } = await supabase
                            .from('chat_messages')
                            .select('id, content, sender:profiles(name)')
                            .eq('id', data.reply_to_id)
                            .single();

                        if (rData) replyInfo = rData;
                    }

                    const finalMessage = { ...data, reply_to: replyInfo };

                    setMessages(prev => {
                        if (prev.find(m => m.id === finalMessage.id)) return prev;
                        if (finalMessage.sender_id === user?.id) {
                            return [...prev.filter(m => !m.id.startsWith('temp-')), finalMessage as any];
                        }
                        return [...prev, finalMessage as any];
                    });
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
        };
    };

    const uploadAttachments = async (): Promise<Attachment[]> => {
        if (pendingAttachments.length === 0) return [];
        const uploadedCallbacks: Attachment[] = [];

        for (const file of pendingAttachments) {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
            const filePath = `${conversationId}/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('chat-attachments')
                .upload(filePath, file);

            if (uploadError) {
                console.error('Upload error:', uploadError);
                alert(`Dosya yüklenemedi: ${uploadError.message}`);
                continue;
            }

            const { data: { publicUrl } } = supabase.storage
                .from('chat-attachments')
                .getPublicUrl(filePath);

            let type: 'image' | 'video' | 'file' | 'audio' = 'file';
            if (file.type.startsWith('image/')) type = 'image';
            else if (file.type.startsWith('video/')) type = 'video';
            else if (file.type.startsWith('audio/') || file.name.match(/\.(webm|mp3|wav|ogg|m4a|mp4)$/i)) type = 'audio';

            uploadedCallbacks.push({
                type,
                url: publicUrl,
                name: file.name,
                size: file.size
            });
        }
        return uploadedCallbacks;
    };

    const handleSendMessage = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if ((!newMessage.trim() && pendingAttachments.length === 0) || !conversationId || !user) return;

        setUploading(true);
        let attachments: Attachment[] = [];
        try {
            attachments = await uploadAttachments();
        } catch (err) {
            console.error(err);
            alert("Dosya yüklenirken hata oluştu.");
            setUploading(false);
            return;
        }

        const msgContent = newMessage.trim();
        const currentReplyTo = replyTo; // Capture for optimistic update

        // Clear inputs immediately
        setNewMessage('');
        setPendingAttachments([]);
        setReplyTo(null);

        // Optimistic Update
        const tempId = `temp-${Date.now()}`;
        const optimisticMsg: Message = {
            id: tempId,
            content: msgContent,
            sender_id: user.id,
            created_at: new Date().toISOString(),
            is_read: false,
            reply_to_id: currentReplyTo?.id,
            reply_to: currentReplyTo ? {
                id: currentReplyTo.id,
                content: currentReplyTo.content,
                sender: { name: currentReplyTo.sender.name }
            } : null,
            attachments: attachments,
            sender: {
                name: user.email?.split('@')[0] || 'Ben',
                avatar: undefined
            }
        };

        setMessages(prev => [...prev, optimisticMsg]);
        scrollToBottom();

        try {
            const { data, error } = await supabase
                .from('chat_messages')
                .insert({
                    conversation_id: conversationId,
                    sender_id: user.id,
                    content: msgContent,
                    reply_to_id: currentReplyTo?.id,
                    attachments: attachments
                })
                .select()
                .single();

            if (error) {
                console.error('Insert Error Detail:', JSON.stringify(error, null, 2));
                setMessages(prev => prev.filter(m => m.id !== tempId));
                throw error;
            }

            // DOUBLE-TAP FIX: 
            // If there are attachments or reply_to_id, force an update immediately.
            // This bypasses potential BEFORE INSERT triggers that might be clearing these fields.
            if (attachments.length > 0 || currentReplyTo?.id) {
                const { error: updateError } = await supabase
                    .from('chat_messages')
                    .update({
                        attachments: attachments,
                        reply_to_id: currentReplyTo?.id
                    })
                    .eq('id', data.id);

                if (updateError) {
                    console.error('Double-Tap Update Error:', updateError);
                }
            }

            // Update optimistic message with real ID
            setMessages(prev => prev.map(m => {
                if (m.id === tempId) {
                    return {
                        ...m,
                        id: data.id,
                        created_at: data.created_at
                    };
                }
                return m;
            }));

            markAsRead(conversationId);
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Mesaj gönderilemedi.');
        } finally {
            setUploading(false);
        }
    };

    const getMessageStatusIcon = (msg: Message) => {
        // If it's a temp optimistic message, show Clock or just 1 tick (sending)
        if (msg.id.startsWith('temp-')) return <div className="text-gray-400 text-[10px]">🕓</div>;

        // If other read it
        if (otherParticipantLastRead && new Date(otherParticipantLastRead) >= new Date(msg.created_at)) {
            // Seen / Read
            return (
                <div className="flex -space-x-1">
                    <span className="text-blue-500 font-bold text-[10px]">✓</span>
                    <span className="text-blue-500 font-bold text-[10px]">✓</span>
                </div>
            );
        }

        // Delivered / Sent (Server confirmed)
        return <span className="text-gray-400 font-bold text-[10px]">✓</span>;
    }

    const renderAttachment = (att: Attachment) => {
        if (att.type === 'image') {
            return (
                <img src={att.url} alt={att.name} className="max-w-full h-auto rounded-lg mb-2 max-h-64 object-cover" />
            );
        } else if (att.type === 'video') {
            return (
                <video src={att.url} controls className="max-w-full h-auto rounded-lg mb-2 max-h-64" />
            );
        } else if (att.type === 'audio') {
            return (
                <audio src={att.url} controls className="w-full mb-2" />
            );
        } else {
            return (
                <a href={att.url} target="_blank" rel="noopener noreferrer" className="flex items-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg mb-2 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                    <File className="w-5 h-5 mr-2 text-gray-500" />
                    <span className="text-sm truncate max-w-[200px] text-blue-600 dark:text-blue-400 hover:underline">{att.name}</span>
                </a>
            );
        }
    };

    if (!conversationId) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800">
                <div className="text-center text-gray-400">
                    <p>Sohbet başlatmak için bir konuşma seçin</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900 z-10">
                <div className="flex items-center space-x-3">
                    <h3 className="font-semibold text-gray-900 dark:text-white">Sohbet</h3>
                </div>
                <div className="flex space-x-2 items-center">
                    {isSearchOpen ? (
                        <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-1 shadow-sm border border-gray-200 dark:border-gray-700">
                            <span className="text-xs text-gray-500 mr-2 min-w-[30px] text-center">
                                {searchMatches.length > 0 ? `${currentMatchIndex + 1}/${searchMatches.length}` : '0/0'}
                            </span>
                            <div className="h-4 w-[1px] bg-gray-300 dark:bg-gray-600 mr-2" />
                            <input
                                autoFocus
                                type="text"
                                className="bg-transparent border-none focus:outline-none text-sm w-32 md:w-48"
                                placeholder="Ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleNextMatch();
                                    }
                                }}
                            />
                            <div className="flex items-center space-x-1 ml-1">
                                <button
                                    onClick={handleNextMatch}
                                    disabled={currentMatchIndex >= searchMatches.length - 1}
                                    className="p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-30"
                                    title="Önceki (Yukarı)"
                                >
                                    <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={handlePrevMatch}
                                    disabled={currentMatchIndex <= 0}
                                    className="p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-30"
                                    title="Sonraki (Aşağı)"
                                >
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                                <button onClick={() => { setIsSearchOpen(false); setSearchQuery(''); }} className="p-1 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setIsSearchOpen(true)}
                            className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                            title="Mesajlarda ara"
                        >
                            <Search className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 p-4 overflow-y-auto bg-gray-50 dark:bg-gray-900/50 space-y-4">
                <div className="flex-1" />

                {loading && messages.length === 0 ? (
                    <div className="flex justify-center p-4"><span className="text-gray-400">Yükleniyor...</span></div>
                ) : (
                    messages.length === 0 ? (
                        <div className="flex justify-center p-10 text-gray-400">Mesaj bulunamadı.</div>
                    ) : (
                        messages.map((msg) => {
                            const isMe = msg.sender_id === user?.id;
                            // Normalize reply_to data (handle array or object)
                            const replyToData = Array.isArray(msg.reply_to) ? msg.reply_to[0] : msg.reply_to;
                            const isMatched = searchMatches[currentMatchIndex] === msg.id;

                            return (
                                <div key={msg.id} id={`message-${msg.id}`} className={`flex ${isMe ? 'justify-end' : 'justify-start'} group transition-all duration-300 rounded-lg p-1 ${isMatched ? 'transition-all' : ''}`}>
                                    <div className={`flex flex-col max-w-[70%] ${isMatched ? 'ring-2 ring-yellow-400 ring-offset-2 rounded-2xl' : ''}`}>
                                        {/* Reply Display */}
                                        {replyToData && (
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    scrollToMessage(replyToData.id);
                                                }}
                                                className={`text-xs mb-1 p-2 rounded-lg border-l-4 opacity-80 cursor-pointer hover:opacity-100 transition-opacity ${isMe ? 'bg-green-50 border-green-500 text-gray-700' : 'bg-gray-100 border-blue-500 text-gray-600'}`}
                                            >
                                                <p className="font-bold">{replyToData.sender?.name || 'Bilinmeyen Kullanıcı'}</p>
                                                <p className="truncate">{replyToData.content || 'Dosya eki'}</p>
                                            </div>
                                        )}

                                        <div className={`rounded-2xl p-3 px-4 shadow-sm relative group-hover:shadow-md transition-all ${isMe
                                            ? 'bg-green-100 text-gray-900 dark:bg-green-900 dark:text-white rounded-br-none'
                                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-none border border-gray-100 dark:border-gray-700'
                                            }`}>

                                            {!isMe && <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">{msg.sender?.name}</p>}

                                            {/* Attachments */}
                                            {msg.attachments && msg.attachments.length > 0 && (
                                                <div className="mb-2">
                                                    {msg.attachments.map((att, idx) => (
                                                        <div key={idx}>{renderAttachment(att)}</div>
                                                    ))}
                                                </div>
                                            )}

                                            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                                                {highlightText(msg.content, searchQuery)}
                                            </p>

                                            <div className={`flex items-center justify-end space-x-1 mt-1 ${isMe ? 'text-green-700 dark:text-green-300' : 'text-gray-400'}`}>
                                                <p className="text-[10px]">
                                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                                {isMe && getMessageStatusIcon(msg)}
                                            </div>

                                            {/* Reply Button (Hover) */}
                                            {!msg.id.startsWith('temp-') && (
                                                <button
                                                    onClick={() => setReplyTo(msg)}
                                                    className={`absolute top-2 ${isMe ? '-left-8' : '-right-8'} opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full transition-opacity`}
                                                >
                                                    <Reply className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 z-10">
                {/* Pending Attachments & Reply Context */}
                {(pendingAttachments.length > 0 || replyTo) && (
                    <div className="mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-between">
                        <div className="flex-1 overflow-hidden">
                            {replyTo && (
                                <div className="border-l-4 border-blue-500 pl-2 text-sm">
                                    <p className="font-bold text-blue-600">Yanıtlanıyor: {replyTo.sender.name}</p>
                                    <p className="text-gray-500 truncate">{replyTo.content || 'Dosya eki'}</p>
                                </div>
                            )}
                            {pendingAttachments.length > 0 && (
                                <div className="flex space-x-2 mt-2">
                                    {pendingAttachments.map((file, i) => (
                                        <div key={i} className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded flex items-center">
                                            <span className="max-w-[100px] truncate">{file.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => { setReplyTo(null); setPendingAttachments([]); }}
                            className="p-1 hover:bg-gray-200 rounded"
                        >
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                    </div>
                )}

                <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                    {isRecording ? (
                        <div className="flex-1 flex items-center justify-between bg-red-50 dark:bg-red-900/20 px-4 py-2 rounded-xl animate-pulse">
                            <div className="flex items-center text-red-600 dark:text-red-400 font-mono">
                                <div className="w-3 h-3 bg-red-600 rounded-full mr-2 animate-bounce" />
                                {formatTime(recordingTime)} Kaydediliyor...
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    type="button"
                                    onClick={cancelRecording}
                                    className="p-1 hover:bg-red-200 dark:hover:bg-red-800 rounded-full text-red-500"
                                    title="İptal Et"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={stopRecording}
                                    className="p-1 hover:bg-green-200 dark:hover:bg-green-800 rounded-full text-green-600"
                                    title="Bitir ve Ekle"
                                >
                                    <StopCircle className="w-6 h-6" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <input
                                type="file"
                                multiple
                                ref={fileInputRef}
                                className="hidden"
                                onChange={(e) => {
                                    if (e.target.files) setPendingAttachments(prev => [...prev, ...Array.from(e.target.files!)]);
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <Paperclip className="w-5 h-5" />
                            </button>
                            <input
                                type="text"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Bir mesaj yazın..."
                                disabled={uploading}
                                className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow disabled:opacity-50"
                            />
                            {(newMessage || pendingAttachments.length > 0) ? (
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {uploading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-5 h-5" />}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={startRecording}
                                    className="p-2.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    <Mic className="w-5 h-5" />
                                </button>
                            )}
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ChatWindow;
